import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './JoinWhiteboard.css';

function JoinWhiteboard() {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sessionExists, setSessionExists] = useState(false);
    const [userName, setUserName] = useState('');
    const [showNameInput, setShowNameInput] = useState(false);
    const wsRef = useRef(null);
    
    useEffect(() => {
        const checkSession = async () => {
            try {
                setLoading(true);
                setError(null);
                
                // Generate a temporary user ID for checking the session
                const tempUserId = `checker_${Math.random().toString(36).substring(2, 10)}`;
                
                // Check if session exists via WebSocket
                const wsURL = `wss://api.andrewslearning.com/wschat/whiteboard/${sessionId}/${tempUserId}/`;
                
                console.log('Checking session via WebSocket:', wsURL);
                
                const ws = new WebSocket(wsURL);
                wsRef.current = ws;
                
                ws.onopen = () => {
                    console.log('WebSocket connection opened for session check');
                    // Send check session request
                    ws.send(JSON.stringify({
                        type: 'check_session',
                        sessionId: sessionId
                    }));
                };
                
                ws.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);
                        console.log('Received WebSocket message:', data);
                        
                        if (data.type === 'connection_established') {
                            console.log('Session exists, showing name input');
                            setSessionExists(true);
                            setShowNameInput(true);
                            setLoading(false);
                            ws.close(); // Close the check connection
                        } else if (data.type === 'session_not_found') {
                            console.log('Session does not exist');
                            setSessionExists(false);
                            setLoading(false);
                            ws.close();
                        }
                    } catch (error) {
                        console.error('Error parsing WebSocket message:', error);
                        setError('Error checking session status');
                        setLoading(false);
                    }
                };
                
                ws.onerror = (error) => {
                    console.error('WebSocket error during session check:', error);
                    setError('Error connecting to server');
                    setLoading(false);
                };
                
                ws.onclose = (event) => {
                    console.log('WebSocket connection closed during session check:', event.code, event.reason);
                };
                
                // Timeout for connection check
                setTimeout(() => {
                    if (loading) {
                        setError('Connection timeout - server may be unavailable');
                        setLoading(false);
                        if (ws.readyState === WebSocket.OPEN) {
                            ws.close();
                        }
                    }
                }, 10000);
                
            } catch (error) {
                console.error('Error checking session:', error);
                setError('Error checking session status');
                setLoading(false);
            }
        };
        
        if (sessionId) {
            checkSession();
        } else {
            setError('No session ID provided');
            setLoading(false);
        }
        
        // Cleanup WebSocket on unmount
        return () => {
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.close();
            }
        };
    }, [sessionId]);
    
    const handleJoinWithName = () => {
        if (!userName.trim()) {
            alert('Please enter your name');
            return;
        }
        
        // Navigate to whiteboard with session ID and user name
        navigate('/whiteboard', {
            state: {
                joinMode: true,
                sessionId: sessionId,
                userName: userName.trim(),
                isHost: false
            }
        });
    };
    
    const handleJoinAnonymous = () => {
        // Navigate to whiteboard with session ID but no name
        navigate('/whiteboard', {
            state: {
                joinMode: true,
                sessionId: sessionId,
                userName: 'Guest',
                isHost: false
            }
        });
    };
    
    const handleCreateNew = () => {
        navigate('/whiteboard');
    };
    
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleJoinWithName();
        }
    };
    
    if (loading) {
        return (
            <div className="join-whiteboard-container">
                <div className="join-whiteboard-card">
                    <h2>Checking Session...</h2>
                    <div className="loading-spinner"></div>
                    <p>Verifying if this whiteboard session exists</p>
                </div>
            </div>
        );
    }
    
    if (error) {
        return (
            <div className="join-whiteboard-container">
                <div className="join-whiteboard-card error">
                    <h2>Connection Error</h2>
                    <p>{error}</p>
                    <div className="join-whiteboard-buttons">
                        <button onClick={handleJoinAnonymous} className="join-button">Try Joining Anyway</button>
                        <button onClick={handleCreateNew} className="create-button">Create New Whiteboard</button>
                    </div>
                </div>
            </div>
        );
    }
    
    if (!sessionExists) {
        return (
            <div className="join-whiteboard-container">
                <div className="join-whiteboard-card not-found">
                    <h2>Session Not Found</h2>
                    <p>The whiteboard session you're trying to join doesn't exist or has expired.</p>
                    <div className="join-whiteboard-buttons">
                        <button onClick={handleJoinAnonymous} className="join-button">Try Joining Anyway</button>
                        <button onClick={handleCreateNew} className="create-button">Create New Whiteboard</button>
                    </div>
                </div>
            </div>
        );
    }
    
    if (showNameInput) {
        return (
            <div className="join-whiteboard-container">
                <div className="join-whiteboard-card">
                    <h2>Join Whiteboard Session</h2>
                    <p>You're about to join an active whiteboard session.</p>
                    <div className="name-input-section">
                        <label htmlFor="userName">Enter your name (optional):</label>
                        <input
                            type="text"
                            id="userName"
                            value={userName}
                            onChange={(e) => setUserName(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Your name"
                            className="name-input"
                            autoFocus
                        />
                    </div>
                    <div className="join-whiteboard-buttons">
                        <button onClick={handleJoinWithName} className="join-button primary">
                            Join with Name
                        </button>
                        <button onClick={handleJoinAnonymous} className="join-button secondary">
                            Join as Guest
                        </button>
                        <button onClick={handleCreateNew} className="create-button">
                            Create New Instead
                        </button>
                    </div>
                </div>
            </div>
        );
    }
    
    return null;
}

export default JoinWhiteboard; 